import {
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PointLight,
  RingGeometry,
  SphereGeometry,
  type Texture,
} from "three";
import type { Body, Catalogue } from "../../data/schema";
import type { FrameData } from "../frame";
import type { TextureManager } from "../textures";
import type { Tier } from "./tier";

export const HIGH_RES_RADII = 20;
/** Outer edge of Saturn's A ring. */
export const RING_OUTER_KM = 140220;
// the ring mesh is built in Saturn radii; this is the equatorial radius from src/data/planets.ts
const SATURN_RADIUS_KM = 60268;
const RING_INNER = 74658 / SATURN_RADIUS_KM; // inner edge of the C ring
const RING_OUTER_RATIO = RING_OUTER_KM / SATURN_RADIUS_KM;

export function wantsHighRes(distance: number, radius: number): boolean {
  return distance < HIGH_RES_RADII * radius;
}

/**
 * Scale for a ring mesh parented to a body mesh of scene radius `radiusScene`, so the ring's outer
 * edge lands at `outerScene`, the mapped distance of RING_OUTER_KM. Radii and moon distances map
 * differently in visual scale; this keeps the ring inside the orbits of the inner moons.
 */
export function ringScale(outerScene: number, radiusScene: number): number {
  return radiusScene > 0 ? outerScene / (RING_OUTER_RATIO * radiusScene) : 1;
}

const sphere = new SphereGeometry(1, 64, 48);
// poles on local Z: the simulation rotates local Z onto each body's pole and RingGeometry lies in XY
sphere.rotateX(Math.PI / 2);

function ringGeometry(): RingGeometry {
  const g = new RingGeometry(RING_INNER, RING_OUTER_RATIO, 128, 1);
  const pos = g.attributes.position,
    uv = g.attributes.uv;
  for (let k = 0; k < pos.count; k++) {
    const r = Math.hypot(pos.getX(k), pos.getY(k));
    uv.setXY(k, (r - RING_INNER) / (RING_OUTER_RATIO - RING_INNER), 0.5);
  }
  return g;
}

interface Entry {
  index: number;
  body: Body;
  mesh: Mesh;
  material: MeshStandardMaterial | MeshBasicMaterial;
  clouds?: Mesh;
  ring?: Mesh;
  highResRequested: boolean;
}

export class TexturedTier implements Tier {
  readonly object = new Group();
  readonly sunLight = new PointLight(0xffffff, 2.5, 0, 0);
  private readonly entries: Entry[] = [];
  private sunIndex = -1;

  constructor(
    bodies: Catalogue,
    indices: number[],
    private readonly textures: TextureManager,
    private readonly mapDistance: (km: number) => number,
  ) {
    this.object.add(this.sunLight);
    for (const index of indices) {
      const body = bodies[index];
      const color = new Color(body.physical.color);
      const material =
        body.kind === "star"
          ? new MeshBasicMaterial({ color })
          : new MeshStandardMaterial({ color, roughness: 1, metalness: 0 });
      const mesh = new Mesh(sphere, material);
      const entry: Entry = { index, body, mesh, material, highResRequested: false };
      if (body.kind === "star") this.sunIndex = index;
      const tex = body.physical.textures;
      if (tex) {
        textures.load(tex.map, (t) => this.setMap(entry, t));
        if (tex.night && material instanceof MeshStandardMaterial) {
          textures.load(tex.night, (t) => {
            material.emissive.set(0xffffff);
            material.emissiveMap = t;
            material.emissiveIntensity = 0.6;
            material.needsUpdate = true;
          });
        }
        if (tex.clouds) {
          const cloudMat = new MeshStandardMaterial({
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
            roughness: 1,
          });
          const clouds = new Mesh(sphere, cloudMat);
          clouds.visible = false;
          clouds.scale.setScalar(1.012);
          mesh.add(clouds);
          entry.clouds = clouds;
          textures.load(tex.clouds, (t) => {
            cloudMat.map = t;
            clouds.visible = true;
            cloudMat.alphaMap = t;
            cloudMat.needsUpdate = true;
          });
        }
        if (tex.ring) {
          const ringMat = new MeshStandardMaterial({
            side: DoubleSide,
            transparent: true,
            roughness: 1,
          });
          const ring = new Mesh(ringGeometry(), ringMat);
          ring.visible = false;
          mesh.add(ring);
          entry.ring = ring;
          textures.load(tex.ring, (t) => {
            ringMat.map = t;
            ring.visible = true;
            ringMat.alphaMap = t;
            // self-lit: near equinox the Sun is in the ring plane and a Lambertian ring would be black
            ringMat.emissive.set(0xffffff);
            ringMat.emissiveMap = t;
            ringMat.emissiveIntensity = 0.8;
            ringMat.needsUpdate = true;
          });
        }
      }
      this.entries.push(entry);
      this.object.add(mesh);
    }
  }

  private setMap(entry: Entry, t: Texture): void {
    entry.material.map = t;
    entry.material.color.set(0xffffff);
    entry.material.needsUpdate = true;
  }

  update(frame: FrameData): void {
    const { positions, radii, rotations, cameraPosition } = frame;
    for (const e of this.entries) {
      const o = 3 * e.index,
        q = 4 * e.index;
      e.mesh.visible = frame.visible[e.index] === 1;
      e.mesh.position.set(positions[o], positions[o + 1], positions[o + 2]);
      e.mesh.quaternion.set(rotations[q], rotations[q + 1], rotations[q + 2], rotations[q + 3]);
      e.mesh.scale.setScalar(radii[e.index]);
      if (e.clouds) e.clouds.rotation.z += 0.0002;
      if (e.ring)
        e.ring.scale.setScalar(ringScale(this.mapDistance(RING_OUTER_KM), radii[e.index]));
      const map8k = e.body.physical.textures?.map8k;
      if (map8k && !e.highResRequested) {
        const dist = Math.hypot(
          positions[o] - cameraPosition.x,
          positions[o + 1] - cameraPosition.y,
          positions[o + 2] - cameraPosition.z,
        );
        if (wantsHighRes(dist, radii[e.index])) {
          e.highResRequested = true;
          this.textures.load(map8k, (t) => this.setMap(e, t));
        }
      }
    }
    if (this.sunIndex >= 0) {
      const o = 3 * this.sunIndex;
      this.sunLight.position.set(positions[o], positions[o + 1], positions[o + 2]);
    }
  }
}
